import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { router } from 'expo-router';
import Head from 'expo-router/head';
import { Fragment, useCallback, useRef } from 'react';
import {
  Image,
  Platform,
  ScrollView,
  View,
  useWindowDimensions
} from 'react-native';
import { useSharedValue } from 'react-native-reanimated';
import Carousel, {
  type ICarouselInstance,
  Pagination
} from 'react-native-reanimated-carousel';
import { SafeAreaView } from 'react-native-safe-area-context';

type Data = {
  id: string;
  image: any;
  title: string;
  text: string;
};

const ONBOARDING_IMAGE_1 = require('../../assets/images/onboard1.png');
const ONBOARDING_IMAGE_2 = require('../../assets/images/onboard2.png');
const ONBOARDING_IMAGE_3 = require('../../assets/images/onboard3.png');

const data: Data[] = [
  {
    id: 'onboard-1',
    image: ONBOARDING_IMAGE_1,
    title: 'Bring Past Conversations to Life',
    text: "Turn your past text conversations into interactive AI personas of anyone you've chatted with."
  },
  {
    id: 'onboard-2',
    image: ONBOARDING_IMAGE_2,
    title: 'Just Record & Upload',
    text: 'Simply screen record a chat from any messaging app (like iMessage, WhatsApp, Slack) and upload it.'
  },
  {
    id: 'onboard-3',
    image: ONBOARDING_IMAGE_3,
    title: 'Continue the Conversation',
    text: ' Sign in, create your first Persona, and continue conversations like never before.'
  }
];

export default function Onboard() {
  const ref = useRef<ICarouselInstance>(null);
  const progress = useSharedValue(0);
  const onPressPagination = (index: number) => {
    ref.current?.scrollTo({
      /**
       * Calculate the difference between the current index and the target index
       * to ensure that the carousel scrolls to the nearest index
       */
      count: index - progress.value,
      animated: true
    });
  };

  const onLast = useCallback(async () => {
    await AsyncStorage.setItem('onboarded', 'true');
    router.navigate('/auth/login');
  }, []);

  const { width } = useWindowDimensions();

  return (
    <Fragment>
      <Head>
        <title>Onboard - lexikon</title>
      </Head>
      <SafeAreaView className="flex-1">
        <ScrollView className="flex-1 self-center pt-12">
          <Carousel
            ref={ref}
            width={width}
            height={480}
            data={data}
            onProgressChange={progress}
            renderItem={({ index }) => {
              const item = data[index];
              return (
                <View className="items-center justify-around bg-background">
                  <Image
                    source={item.image}
                    style={{ height: Platform.OS === 'android' ? 130 : 300 }}
                    resizeMode="contain"
                  />

                  <View className="mt-12">
                    <Text className="mb-4 px-4 text-center text-xl font-semibold">
                      {item.title}
                    </Text>
                    <Text className="mx-4 px-4 text-center leading-7">
                      {item.text}
                    </Text>
                  </View>
                </View>
              );
            }}
          />

          <Pagination.Basic
            progress={progress}
            data={data}
            dotStyle={{
              backgroundColor: 'rgba(255,255,255,0.2)',
              borderRadius: 50
            }}
            activeDotStyle={{
              backgroundColor: '#8FF3E7',
              borderRadius: 50
            }}
            containerStyle={{ gap: 15, marginTop: 40 }}
            onPress={onPressPagination}
          />
        </ScrollView>

        <View className="flex self-stretch px-8 pb-4">
          <Button
            size="lg"
            className="mt-4 w-full rounded-full web:self-center web:lg:w-[50%]"
            onPress={onLast}
          >
            <Text>Get Started</Text>
          </Button>

          <Button
            size="lg"
            variant="outline"
            className="mt-4 w-full rounded-full web:self-center web:lg:w-[50%]"
            onPress={onLast}
          >
            <Text>Log In</Text>
          </Button>
        </View>
      </SafeAreaView>
    </Fragment>
  );
}
